import { z } from "zod";

// ===== Unified staff task inbox =====
// A general, assignable to-do — the clinic's "what needs my attention" worklist.
// Optionally tied to a patient and/or an originating resource. Distinct from
// notifications (informational): a task has an owner, a status, a priority, a due
// date, and stays until closed.

export const taskStatusSchema = z.enum([
	"open",
	"in_progress",
	"done",
	"cancelled",
]);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

export const taskPrioritySchema = z.enum(["low", "normal", "high", "urgent"]);
export type TaskPriority = z.infer<typeof taskPrioritySchema>;

export const taskCategorySchema = z.enum([
	"general",
	"follow_up",
	"callback",
	"refill",
	"lab_review",
	"radiology_review",
	"referral",
	"billing",
	"admin",
]);
export type TaskCategory = z.infer<typeof taskCategorySchema>;

export const taskApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	title: z.string(),
	description: z.string().nullable(),
	status: taskStatusSchema,
	priority: taskPrioritySchema,
	category: taskCategorySchema,
	assignee_member_id: z.string().uuid().nullable(),
	assignee_name: z.string().nullable(),
	assigned_role: z.string().nullable(),
	patient_id: z.string().uuid().nullable(),
	patient_name: z.string().nullable(),
	resource_type: z.string().nullable(),
	resource_id: z.string().uuid().nullable(),
	due_at: z.string().nullable(),
	created_by_member_id: z.string().uuid().nullable(),
	created_by_name: z.string().nullable(),
	completed_at: z.string().nullable(),
	created_at: z.string(),
});
export type TaskApi = z.infer<typeof taskApiSchema>;

export const createTaskInputSchema = z.object({
	title: z.string().min(1).max(300),
	description: z.string().max(2000).nullable().optional(),
	priority: taskPrioritySchema.optional(),
	category: taskCategorySchema.optional(),
	assignee_member_id: z.string().uuid().nullable().optional(),
	assigned_role: z.string().max(40).nullable().optional(),
	patient_id: z.string().uuid().nullable().optional(),
	resource_type: z.string().max(60).nullable().optional(),
	resource_id: z.string().uuid().nullable().optional(),
	due_at: z.string().datetime().nullable().optional(),
});
export type CreateTaskInput = z.infer<typeof createTaskInputSchema>;

export const updateTaskInputSchema = z.object({
	title: z.string().min(1).max(300).optional(),
	description: z.string().max(2000).nullable().optional(),
	status: taskStatusSchema.optional(),
	priority: taskPrioritySchema.optional(),
	category: taskCategorySchema.optional(),
	// null = unassign (back to the pool).
	assignee_member_id: z.string().uuid().nullable().optional(),
	assigned_role: z.string().max(40).nullable().optional(),
	due_at: z.string().datetime().nullable().optional(),
});
export type UpdateTaskInput = z.infer<typeof updateTaskInputSchema>;

/** Inbox scope: my queue, the unassigned pool, or everything. */
export const taskScopeSchema = z.enum(["mine", "unassigned", "all"]);
export type TaskScope = z.infer<typeof taskScopeSchema>;

export const listTasksQuerySchema = z.object({
	scope: taskScopeSchema.optional(),
	// 'active' (open + in_progress) is the default; or a specific status.
	status: z
		.enum(["active", "open", "in_progress", "done", "cancelled"])
		.optional(),
	patient_id: z.string().uuid().optional(),
	limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;

/** Badge counts for the inbox. */
export const taskCountsSchema = z.object({
	/** Active tasks assigned to me. */
	mine: z.number(),
	/** Active tasks in the unassigned pool. */
	unassigned: z.number(),
	/** Active tasks that are overdue (due_at in the past). */
	overdue: z.number(),
});
export type TaskCounts = z.infer<typeof taskCountsSchema>;
